import { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { JOBS_TOOLBAR_BUTTON_WIDTH } from "./jobDashboardLayout";
import {
  EMPTY_FILTERS,
  JOB_STATUSES,
  TIME_LABELS,
  timeBounds,
} from "./jobFilters";

export default function JobFilters({ filters, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState({ from: "", to: "" });
  const [error, setError] = useState("");
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const applyTime = (time) => {
    onChange({ ...filters, time });
    setAnchor(null);
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "nowrap",
        gap: 2,
        pt: 2,
        alignItems: "center",
      }}
    >
      <Button
        sx={{
          width: JOBS_TOOLBAR_BUTTON_WIDTH,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
        onClick={() => onChange(EMPTY_FILTERS)}
      >
        Reset filters
      </Button>
      <TextField
        size="small"
        label="Name"
        placeholder="Search by name…"
        value={filters.name}
        onChange={(e) => onChange({ ...filters, name: e.target.value })}
        sx={{ minWidth: 240, maxWidth: 480, flex: "1 1 240px" }}
      />
      <FormControl
        size="small"
        sx={{ minWidth: 220, maxWidth: 360, flex: "1 1 220px" }}
      >
        <InputLabel id="job-status-filter-label" shrink>
          Status
        </InputLabel>
        <Select
          labelId="job-status-filter-label"
          label="Status"
          multiple
          displayEmpty
          value={filters.statuses}
          onChange={(e) => onChange({ ...filters, statuses: e.target.value })}
          renderValue={(values) =>
            values.length ? values.join(", ") : "All statuses"
          }
        >
          {JOB_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              <Checkbox checked={filters.statuses.includes(status)} />
              <ListItemText primary={status} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button
        sx={{ width: 400, flexShrink: 0, whiteSpace: "nowrap" }}
        variant="outlined"
        onClick={(e) => {
          setDraft({
            from: filters.time.from || "",
            to: filters.time.to || "",
          });
          setError("");
          setAnchor(e.currentTarget);
        }}
      >
        Submitted:{" "}
        {TIME_LABELS[filters.time.preset] ||
          `${filters.time.from || "Any date"} – ${filters.time.to || "Any date"}`}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box
          sx={{
            p: 2,
            maxWidth: 400,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="subtitle2">
            Submitted time · {timezone}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Object.entries(TIME_LABELS).map(([preset, label]) => (
              <Button key={preset} onClick={() => applyTime({ preset })}>
                {label}
              </Button>
            ))}
          </Box>
          <TextField
            label="From"
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
          <TextField
            label="To (inclusive)"
            type="date"
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
          <Typography variant="caption">
            Leave either date empty for an open-ended range.
          </Typography>
          {error && (
            <Typography role="alert" color="error">
              {error}
            </Typography>
          )}
          <Button
            onClick={() => {
              try {
                const time = { preset: "custom", ...draft };
                timeBounds(time);
                applyTime(time);
              } catch (err) {
                setError(err.message);
              }
            }}
          >
            Apply range
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}
JobFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};
